import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api/client';
import { CameraDevice } from '../../types';
import { MapPin, Navigation, Info, Loader2, Satellite, Flame } from 'lucide-react';

const MAPS_KEY = (import.meta as any).env?.VITE_GOOGLE_MAPS_KEY as string | undefined;
// Monrovia, Liberia — where the fleet is, the default center when nothing is pinned.
const DEFAULT_CENTER = { lat: 6.3006, lng: -10.7969 };

// The visualization library is needed for the heatmap layer.
function loadMaps(key: string): Promise<any> {
  const w = window as any;
  if (w.google?.maps) return Promise.resolve(w.google);
  return new Promise((resolve, reject) => {
    const existing = document.getElementById('gmaps-js') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(w.google));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = 'gmaps-js';
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=visualization`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(w.google);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

const statusOf = (d: CameraDevice): { color: string; label: string } =>
  d.status === 'online' ? { color: '#22c55e', label: 'Online' }
    : d.eye4State === 'active' ? { color: '#94a3b8', label: 'Quiet (reachable)' }
      : d.eye4State === 'inactivated' ? { color: '#ef4444', label: 'Dead — needs reactivation' }
        : { color: '#ef4444', label: 'Offline' };

// Heat weight: down/dead cameras run hot, quiet warm, online barely registers —
// so the heatmap highlights where problems concentrate, not just where cameras are.
const heatWeight = (d: CameraDevice): number =>
  d.status === 'online' ? 0.4 : d.eye4State === 'active' ? 1 : 3;

// A camera-shaped marker (body + lens) on a pin point, colored by status. Built
// as an inline SVG data-URI so it reads on satellite imagery; anchored at the
// bottom point so it sits on the coordinate.
function pinIcon(google: any, color: string) {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">` +
    `<path d="M18 46 L10 30 h16 z" fill="${color}"/>` +
    `<rect x="3" y="9" width="30" height="22" rx="5" fill="${color}" stroke="#0b1120" stroke-width="1.6"/>` +
    `<rect x="12" y="5.5" width="11" height="5" rx="2" fill="${color}" stroke="#0b1120" stroke-width="1.2"/>` +
    `<circle cx="18" cy="20" r="6.5" fill="#0b1120" fill-opacity="0.9"/>` +
    `<circle cx="18" cy="20" r="3.6" fill="#ffffff"/>` +
    `</svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(36, 46),
    anchor: new google.maps.Point(18, 46),
  };
}

function infoHtml(d: CameraDevice): string {
  const s = statusOf(d);
  const esc = (x: string) => (x || '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string));
  return `
    <div style="font-family:system-ui;min-width:190px;color:#0f172a">
      <div style="font-weight:700;font-size:13px;margin-bottom:2px">${esc(d.name) || '(unnamed)'}</div>
      <div style="font-family:monospace;font-size:11px;color:#2563eb;margin-bottom:6px">${esc(d.serial)}</div>
      <div style="display:flex;align-items:center;gap:6px;font-size:12px;margin-bottom:4px">
        <span style="width:9px;height:9px;border-radius:50%;background:${s.color};display:inline-block"></span>${s.label}
      </div>
      <div style="font-size:11.5px;color:#475569">${esc(d.ownerName)}</div>
      <div style="font-size:11.5px;color:#475569">${esc(d.model || '—')} · ${d.connectionType ? d.connectionType.toUpperCase() : '—'}</div>
    </div>`;
}

export const CameraMapPage: React.FC = () => {
  const [located, setLocated] = useState<CameraDevice[]>([]);
  const [totalCameras, setTotalCameras] = useState<number | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'pins' | 'heatmap'>('pins');
  // Flips true once the map + markers exist, so the layer-apply runs from a
  // post-commit effect (a later tick) rather than inside initMap's own stack —
  // Google Maps won't paint markers added synchronously with map creation until
  // the next tick, which is why pins only appeared after a manual view toggle.
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapObj = useRef<any>(null);
  const markers = useRef<any[]>([]);
  const heatmap = useRef<any>(null);
  const viewRef = useRef(view);

  useEffect(() => {
    (async () => {
      try {
        const [locs, totals] = await Promise.all([
          api.getLocatedDevices(),
          api.getFleetTotals().catch(() => null),
        ]);
        setLocated(locs);
        setTotalCameras(totals?.cameras ?? null);
      } catch (err) {
        console.error('Failed to load located devices', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Show markers OR the heatmap for the current view; a no-op until the map built.
  const applyLayers = useCallback(() => {
    const map = mapObj.current;
    if (!map) return;
    const pins = viewRef.current === 'pins';
    markers.current.forEach((m) => m.setMap(pins ? map : null));
    if (heatmap.current) heatmap.current.setMap(pins ? null : map);
  }, []);

  useEffect(() => { viewRef.current = view; applyLayers(); }, [view, applyLayers, mapReady]);

  const initMap = useCallback(async () => {
    if (!MAPS_KEY) { setMapError('No Google Maps key configured (set VITE_GOOGLE_MAPS_KEY).'); return; }
    if (!mapRef.current) return;
    let google: any;
    try {
      google = await loadMaps(MAPS_KEY);
    } catch {
      setMapError('Google Maps failed to load (check the key / network).');
      return;
    }
    const map = new google.maps.Map(mapRef.current, {
      center: DEFAULT_CENTER,
      zoom: located.length ? 14 : 7,
      mapTypeId: google.maps.MapTypeId.HYBRID, // satellite + labels
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        mapTypeIds: ['hybrid', 'satellite', 'roadmap'],
      },
      streetViewControl: false,
      tilt: 0,
    });
    mapObj.current = map;

    const info = new google.maps.InfoWindow();
    const bounds = new google.maps.LatLngBounds();
    const points: any[] = [];
    markers.current = [];
    located.forEach((d) => {
      if (d.latitude == null || d.longitude == null) return;
      const pos = { lat: d.latitude, lng: d.longitude };
      const s = statusOf(d);
      const marker = new google.maps.Marker({
        position: pos, title: `${d.name || d.serial} — ${s.label}`,
        icon: pinIcon(google, s.color),
      });
      marker.addListener('click', () => { info.setContent(infoHtml(d)); info.open({ map, anchor: marker }); });
      markers.current.push(marker);
      points.push({ location: new google.maps.LatLng(d.latitude, d.longitude), weight: heatWeight(d) });
      bounds.extend(pos);
    });

    // Camera-density heatmap (each located camera = one point). Grows meaningful
    // as more cameras get coordinates. A vivid, high-contrast gradient and a low
    // maxIntensity keep it sharp even while the data is sparse.
    if (google.maps.visualization && points.length) {
      heatmap.current = new google.maps.visualization.HeatmapLayer({
        data: points,
        radius: 26,
        opacity: 0.95,
        dissipating: true,
        maxIntensity: 5,
        gradient: [
          'rgba(0, 0, 255, 0)',
          'rgba(0, 120, 255, 1)',
          'rgba(0, 220, 255, 1)',
          'rgba(0, 255, 120, 1)',
          'rgba(170, 255, 0, 1)',
          'rgba(255, 230, 0, 1)',
          'rgba(255, 150, 0, 1)',
          'rgba(255, 60, 0, 1)',
          'rgba(210, 0, 0, 1)',
        ],
      });
    }

    applyLayers(); // set the current view's layer
    if (located.length === 1) { map.setCenter(bounds.getCenter()); map.setZoom(17); }
    else if (located.length > 1) map.fitBounds(bounds, 80);
    setMapReady(true); // re-apply layers from a post-commit tick so pins paint immediately
  }, [located, applyLayers]);

  useEffect(() => { if (!loading) initMap(); }, [loading, initMap]);

  const Tab: React.FC<{ v: 'pins' | 'heatmap'; icon: React.ReactNode; label: string }> = ({ v, icon, label }) => (
    <button
      onClick={() => setView(v)}
      className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-secondary'}`}
    >
      {icon} <span>{label}</span>
    </button>
  );

  return (
    <div>
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-lg)', padding: '14px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Navigation size={18} color="var(--primary-light)" />
          <div>
            <strong style={{ fontSize: 13.5 }}>
              {located.length} of {totalCameras ?? '—'} cameras geo-located
            </strong>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              {view === 'heatmap'
                ? 'Heatmap is weighted by status — down/dead cameras run hot, so it shows where problems concentrate. Fills in as more get coordinates.'
                : 'Pins show each geo-located camera, colored by status. Click a pin for details.'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tab v="pins" icon={<MapPin size={13} />} label="Pins" />
          <Tab v="heatmap" icon={<Flame size={13} />} label="Heatmap" />
          {view === 'pins' && (
            <span style={{ display: 'flex', gap: 12, fontSize: 11.5, color: 'var(--text-muted)', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-dim)' }}><Satellite size={13} /> Satellite</span>
              <Legend color="#22c55e" label="Online" />
              <Legend color="#94a3b8" label="Quiet" />
              <Legend color="#ef4444" label="Down" />
            </span>
          )}
        </div>
      </div>

      <div style={{ position: 'relative', height: 620, borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border-dim)', background: 'var(--bg-surface)' }}>
        {loading ? (
          <Center><Loader2 size={22} className="spin" /></Center>
        ) : mapError ? (
          <Center><div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', maxWidth: 360 }}><Info size={18} style={{ marginBottom: 8 }} /><div>{mapError}</div></div></Center>
        ) : null}
        <div ref={mapRef} style={{ width: '100%', height: '100%', display: loading || mapError ? 'none' : 'block' }} />
        {!loading && !mapError && located.length === 0 && (
          <Center><div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}><MapPin size={20} style={{ marginBottom: 8 }} /><div>No cameras are geo-located yet.</div></div></Center>
        )}
      </div>
    </div>
  );
};

const Center: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{children}</div>
);
const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
    <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} /> {label}
  </span>
);
